import React, { useEffect, useMemo, useState } from "react";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Container from "react-bootstrap/Container";
import logo from "assets/r4b-main.png";
import { Link, useLocation } from "react-router-dom";
// Navbar.Collapse from RBv2 uses react-transition-group and can stay in
// "exited" (display:none) even when expanded=true. Use a plain div with
// Bootstrap classes instead so Bootstrap's own CSS controls visibility.
import {
  AiOutlineUser,
  AiOutlineLogout,
  AiOutlineLogin,
  AiOutlinePrinter,
  AiFillDashboard,
  AiOutlineAccountBook,
  AiFillAmazonSquare,
  AiOutlineCheckCircle,
  AiOutlineShoppingCart,
  AiOutlineCoffee,
} from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { UserLevel } from "common/userLevels";

const MD_BREAKPOINT = 768;

const ALL_STAFF = [
  UserLevel.COUNTER_STAFF,
  UserLevel.ADMIN,
  UserLevel.OUTLET_MANAGER,
  UserLevel.SUPER_ADMIN,
];

const navigationConfig = [
  {
    to: "/dashboard",
    text: "Dashboard",
    levels: [UserLevel.ADMIN, UserLevel.OUTLET_MANAGER, UserLevel.SUPER_ADMIN],
    icon: <AiFillDashboard style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/quick-order",
    text: "Quick Order",
    levels: ALL_STAFF,
    icon: <AiOutlineUser style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/order",
    text: "Order",
    levels: [
      UserLevel.COUNTER_STAFF,
      UserLevel.CASHIER,
      UserLevel.DELIVERY,
      UserLevel.OUTLET_MANAGER,
      UserLevel.SUPER_ADMIN,
    ],
    icon: <AiOutlineAccountBook style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/consumer",
    text: "Consumer",
    levels: [
      UserLevel.COUNTER_STAFF,
      UserLevel.WAITER,
      UserLevel.OUTLET_MANAGER,
      UserLevel.SUPER_ADMIN,
    ],
    icon: <AiOutlineUser style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/queue",
    text: "Queue",
    levels: [
      UserLevel.COUNTER_STAFF,
      UserLevel.BAKER,
      UserLevel.OUTLET_MANAGER,
      UserLevel.SUPER_ADMIN,
    ],
    icon: <AiOutlineCoffee style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/product",
    text: "Product",
    levels: ALL_STAFF,
    icon: <AiFillAmazonSquare style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/print",
    text: "Print",
    levels: [
      UserLevel.COUNTER_STAFF,
      UserLevel.CASHIER,
      UserLevel.OUTLET_MANAGER,
      UserLevel.SUPER_ADMIN,
    ],
    icon: <AiOutlinePrinter style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/order-checker",
    text: "Order Checker",
    levels: [UserLevel.ORDER_CHECKER, UserLevel.SUPER_ADMIN],
    icon: <AiOutlineCheckCircle style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/buy",
    text: "Buy",
    levels: [UserLevel.CUSTOMER],
    icon: <AiOutlineShoppingCart style={{ marginBottom: "2px" }} />,
  },
  {
    to: "/buy/order",
    text: "Place Order",
    levels: [UserLevel.CUSTOMER],
    icon: <AiOutlineUser style={{ marginBottom: "2px" }} />,
  },
];

function NavBar({ currentPathName, onLogout, isLoggedIn, userLevel }) {
  const { pathname } = useLocation();
  const isHomePage = pathname === "/";
  const [navColour, updateNavbar] = useState(false);
  // Separate desktop detection from the mobile open/close toggle so we never
  // accidentally unmount the Collapse on desktop (RBv2 unmounts when expanded=false).
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= MD_BREAKPOINT,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    function onScroll() {
      updateNavbar(window.scrollY >= 20);
    }
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    function onResize() {
      setIsDesktop(window.innerWidth >= MD_BREAKPOINT);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Desktop: always keep Collapse mounted (expanded=true).
  // Mobile: controlled by the hamburger toggle.
  const navExpanded = isDesktop || mobileOpen;

  const filteredNavigation = useMemo(() => {
    if (!isLoggedIn || !userLevel) return [];
    return navigationConfig.filter((item) => item.levels.includes(userLevel));
  }, [isLoggedIn, userLevel]);

  function closeOnMobile() {
    setMobileOpen(false);
  }

  return (
    <Navbar
      expanded={navExpanded}
      onToggle={setMobileOpen}
      expand="md"
      fixed="top"
      className={navColour ? "sticky" : "navbar"}
    >
      <Container>
        <Navbar.Brand href="/" className="d-flex">
          <img src={logo} className="logo" alt="brand" />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="responsive-navbar-nav">
          <span></span>
          <span></span>
          <span></span>
        </Navbar.Toggle>

        <Navbar.Collapse id="responsive-navbar-nav">
          <Nav className="ms-auto" defaultActiveKey="#home">
            {isHomePage && isLoggedIn && (
              <Nav.Item>
                <Nav.Link as={Link} to="/quick-order" onClick={closeOnMobile}>
                  <div className="navLink">
                    <AiFillDashboard style={{ marginBottom: "2px" }} />
                    {t("Dashboard")}
                  </div>
                </Nav.Link>
              </Nav.Item>
            )}
            {filteredNavigation.map((item) => (
              <Nav.Item key={item.text}>
                <Nav.Link as={Link} to={item.to} onClick={closeOnMobile}>
                  <div className="navLink">
                    {item.icon} {t(item.text)}
                  </div>
                </Nav.Link>
              </Nav.Item>
            ))}

            {isLoggedIn ? (
              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/login"
                  onClick={() => {
                    closeOnMobile();
                    onLogout();
                  }}
                >
                  <div className="navLink">
                    <AiOutlineLogout style={{ marginBottom: "2px" }} />
                    {t("Logout")}
                  </div>
                </Nav.Link>
              </Nav.Item>
            ) : (
              currentPathName && (
                <Nav.Item>
                  <Nav.Link as={Link} to="/login" onClick={closeOnMobile}>
                    <div className="navLink">
                      <AiOutlineLogin style={{ marginBottom: "2px" }} />
                      {t("Login")}
                    </div>
                  </Nav.Link>
                </Nav.Item>
              )
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default NavBar;
